import { useEffect, useRef } from "react";
import { usePlans } from "@/hooks/api/useBilling";
import { trackGA4Event } from "@/lib/analytics";
import type { Plan } from "@/types/api";
import { Link } from "react-router-dom";
import type { ReactNode } from "react";
import { PageMeta } from "@/components/PageMeta";
import { JsonLd } from "@/components/JsonLd";
import { canonicalUrl } from "@/lib/canonical";
import FieldHeader from "@/components/field/FieldHeader";
import FieldFooter from "@/components/field/FieldFooter";
import "@/styles/design-tokens.css";
import "./pricing.css";

interface PlanFeature { text: string; included: boolean; }
interface PricingTier {
  name: string;
  price: string;
  priceDetail?: string;
  description: string;
  features: PlanFeature[];
  ctaHref: string;
  highlighted?: boolean;
  tier: string;
}

const FALLBACK_TIERS: PricingTier[] = [
  {
    name: "Free",
    tier: "free",
    price: "$0",
    priceDetail: "forever",
    description: "Build your PoC without worrying about costs",
    features: [
      { text: "10,000 requests/month", included: true },
      { text: "10 req/sec rate limit", included: true },
      { text: "All Lookup APIs", included: true },
      { text: "MCP Integration", included: true },
      { text: "10 API keys", included: true },
      { text: "Community support", included: true },
    ],
    ctaHref: "/dashboard",
    highlighted: true,
  },
];

function formatPrice(priceCents: number): string {
  if (priceCents === 0) return "$0";
  return `$${priceCents / 100}`;
}

function formatRequests(count: number): string {
  if (count === 0) return "Unlimited";
  if (count >= 1000000) return `${count / 1000000}M requests/month`;
  if (count >= 1000) return `${(count / 1000).toLocaleString()}k requests/month`;
  return `${count.toLocaleString()} requests/month`;
}

function planToTier(plan: Plan, index: number, totalPublicPlans: number): PricingTier {
  const features: PlanFeature[] = [];
  features.push({ text: formatRequests(plan.quotas.requests_per_month), included: true });
  if (plan.tier !== "enterprise") {
    const rpm = plan.rate_limits?.rpm || 600;
    features.push({ text: `${Math.round(rpm / 60)} req/sec rate limit`, included: true });
  } else {
    features.push({ text: "Custom rate limits", included: true });
  }
  features.push({ text: "All Lookup APIs", included: true });
  if (plan.features.batch_endpoints || plan.features.mcp_access) {
    features.push({ text: "MCP Integration", included: true });
  }
  if (plan.quotas.credentials > 0) {
    features.push({ text: `${plan.quotas.credentials} API keys`, included: true });
  } else if (plan.tier === "enterprise") {
    features.push({ text: "Unlimited API keys", included: true });
  }
  if (plan.quotas.shl_active_limit > 0) {
    features.push({ text: `${plan.quotas.shl_active_limit.toLocaleString()} active SMART Health Links`, included: true });
  } else if (plan.tier === "enterprise") {
    features.push({ text: "Unlimited SMART Health Links", included: true });
  }
  if (plan.quotas.members > 1) {
    features.push({ text: `${plan.quotas.members} team members`, included: true });
  } else if (plan.quotas.members === 0 && plan.tier === "enterprise") {
    features.push({ text: "Unlimited team members", included: true });
  }
  if (plan.features.priority_support) features.push({ text: "Priority support", included: true });
  if (plan.features.sla || plan.features.sla_guarantee) features.push({ text: "SLA guarantee", included: true });

  return {
    name: plan.name,
    tier: plan.tier,
    price: plan.tier === "enterprise" ? "Custom" : formatPrice(plan.price_cents),
    priceDetail: plan.price_cents === 0 ? "forever" : "/month",
    description: plan.description,
    features,
    ctaHref: plan.tier === "enterprise" ? "mailto:enterprise@fhirfly.io" : "/dashboard",
    highlighted: totalPublicPlans === 1 ? true : index === Math.floor(totalPublicPlans / 2),
  };
}

const faqsJsonLd = [
  { question: "What happens if I exceed my monthly quota?", answer: "Additional requests will be rejected with a 429 status code until your quota resets at the start of your next billing period. We'll send you alerts at 80% and 90% usage so you know when you're approaching your limit." },
  { question: "Is the data really free elsewhere?", answer: "Yes! NDC, NPI, RxNorm, LOINC, and other reference data is available from government sources. FHIRfly saves you time by unifying these sources into a single, consistent API with better developer experience, reliability, and support." },
  { question: "Can I upgrade or downgrade my plan?", answer: "Yes! You can upgrade or downgrade your plan at any time from the dashboard. When upgrading, you'll be charged a prorated amount. When downgrading, you'll receive credit toward your next billing cycle." },
  { question: "Can I use this in production?", answer: "The free tier is great for development and low-volume production use. For higher-volume production workloads, consider our Developer, Pro, or Business tiers with increased limits and SLAs." },
  { question: "What are SMART Health Links?", answer: "SMART Health Links (SHL) let patients share encrypted health records via QR codes and short links. FHIRfly provides free hosted storage for SHL content with per-plan limits on active links." },
];

const faqs: { question: string; answer: ReactNode }[] = [
  ...faqsJsonLd.slice(0, 4),
  {
    question: "What are SMART Health Links?",
    answer: (
      <>
        SMART Health Links (SHL) let patients share encrypted health records via QR codes and short links.
        FHIRfly provides free hosted storage for SHL content with per-plan limits on active links. See the{" "}
        <Link to="/docs/shl/overview">SHL documentation</Link> for details.
      </>
    ),
  },
];

export default function PricingPage() {
  const { data: plansData, isLoading, error } = usePlans();
  const viewItemListFired = useRef(false);

  const plans = plansData?.plans || [];
  const tiers: PricingTier[] = plans.length > 0
    ? plans.sort((a, b) => a.display_order - b.display_order).map((plan, index) => planToTier(plan, index, plans.length))
    : FALLBACK_TIERS;
  const displayTiers = error ? FALLBACK_TIERS : tiers;
  const hasMultipleTiers = displayTiers.length > 1;

  useEffect(() => {
    if (viewItemListFired.current || displayTiers.length === 0) return;
    viewItemListFired.current = true;
    trackGA4Event("view_item_list", {
      item_list_id: "pricing_plans",
      item_list_name: "Pricing Plans",
      items: displayTiers.map((tier, index) => ({ item_id: tier.tier, item_name: tier.name, price: parseFloat(tier.price.replace("$", "")) || 0, index })),
    });
  }, [displayTiers]);

  function handleSelectPlan(tier: PricingTier) {
    trackGA4Event("select_item", {
      item_list_id: "pricing_plans",
      item_list_name: "Pricing Plans",
      items: [{ item_id: tier.tier, item_name: tier.name, price: parseFloat(tier.price.replace("$", "")) || 0 }],
    });
  }

  return (
    <div className="ffly ffly--public pricing-page">
      <PageMeta
        title="Pricing"
        description="Simple, transparent pricing for FHIRfly healthcare APIs. Start free with 10,000 requests per month. Upgrade anytime for higher quotas, priority support, and SLA guarantees."
        canonical={canonicalUrl("/pricing")}
      />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqsJsonLd.map((faq) => ({ "@type": "Question", name: faq.question, acceptedAnswer: { "@type": "Answer", text: faq.answer } })),
      }} />

      <FieldHeader active="pricing" />

      <div className="wrap">
        <div className="masthead">
          <div className="eyebrow">Pricing</div>
          <h1>{hasMultipleTiers ? "Simple, transparent pricing" : "Start building for free"}</h1>
          <p className="lede">{hasMultipleTiers ? "Choose the plan that fits. Upgrade or downgrade anytime — every code system included on every plan." : "Everything you need to integrate healthcare reference data."}</p>
        </div>

        <div className="plans">
          {isLoading
            ? [1, 2, 3].map((i) => <div key={i} className="sk" />)
            : displayTiers.map((tier) => (
                <div key={tier.name} className={`card${tier.highlighted ? " hot" : ""}`}>
                  {tier.highlighted && <div className="pop">Most popular</div>}
                  <div className="nm">{tier.name}</div>
                  <div className="price"><span className="amt">{tier.price}</span>{tier.priceDetail && <span className="per">{tier.priceDetail}</span>}</div>
                  <p className="desc">{tier.description}</p>
                  <ul>
                    {tier.features.map((f, idx) => (
                      <li key={idx}><i className="ck fa-solid fa-check" /><span>{f.text}</span></li>
                    ))}
                  </ul>
                  <a className={`cta ${tier.highlighted ? "primary" : "neutral"}`} href={tier.ctaHref} onClick={() => handleSelectPlan(tier)}>
                    {tier.tier === "enterprise" ? "Contact us" : tier.price === "$0" ? "Get started free" : `Start with ${tier.name}`}
                  </a>
                </div>
              ))}
        </div>

        <div className="include">
          <h2>Every plan includes</h2>
          <div className="grid">
            {[
              { icon: "fa-solid fa-code", text: "Full SDK access (npm)" },
              { icon: "fa-solid fa-robot", text: "MCP integration for AI assistants" },
              { icon: "fa-solid fa-book", text: "Complete documentation" },
              { icon: "fa-solid fa-shapes", text: "All response shapes" },
              { icon: "fa-solid fa-lock", text: "Secure API authentication" },
              { icon: "fa-solid fa-link", text: "SMART Health Links hosting" },
            ].map((item) => (
              <div key={item.text} className="item"><i className={item.icon} /><span>{item.text}</span></div>
            ))}
          </div>
        </div>

        <div className="faq">
          <h2>Frequently asked questions</h2>
          {faqs.map((faq, idx) => (
            <details key={idx}>
              <summary>{faq.question}<span className="chev">›</span></summary>
              <div className="ans">{faq.answer}</div>
            </details>
          ))}
        </div>

        <div className="endcta">
          <h2>Ready to get started?</h2>
          <p>Create a free account and start making API calls in minutes.</p>
          <div className="btns">
            <a className="cta primary" href="/dashboard" style={{ maxWidth: 260 }}>Get started free</a>
            <a className="cta neutral" href="/docs" style={{ maxWidth: 260 }}>Read the docs</a>
          </div>
        </div>
      </div>

      <FieldFooter variant="full" />
    </div>
  );
}
